import { type SEOHandle } from '@nasa-gcn/remix-seo';
import {
  json,
  type LoaderFunctionArgs,
  type MetaFunction,
} from '@remix-run/node';
import { Link, useLoaderData } from '@remix-run/react';
import { DuneEmbed } from '#app/components/dune/dune-embed';
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx';
import { Icon } from '#app/components/ui/icon.tsx';
import { defaultThemes, ProjectSchema } from '#app/utils/sandpack.ts';

export async function loader({ params }: LoaderFunctionArgs) {
  try {
    const rawProject = JSON.parse(
      Buffer.from(params['*'] as string, 'base64').toString('utf8'),
    ) as Record<string, any>;

    // Explicitly include an `id` to
    // account for legacy projects.
    const project = ProjectSchema.parse({ id: 'project', ...rawProject });

    const theme =
      defaultThemes[project.display.general.theme as keyof typeof defaultThemes]
        ?.theme ?? defaultThemes.kempoLight.theme;

    return json({ project, theme });
  } catch (error) {
    console.error(error);

    throw error;
  }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [{ title: `${data?.project.name ?? 'View Project'} | Kempo` }];
};

export const handle: SEOHandle = {
  getSitemapEntries() {
    return null;
  },
};

export default function ViewProject() {
  const { project, theme } = useLoaderData<typeof loader>();
  return <DuneEmbed {...project} theme={theme} />;
}

export function ErrorBoundary() {
  return (
    <GeneralErrorBoundary
      unexpectedErrorHandler={() => (
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-3">
            <h1>Failed to load project</h1>
          </div>
          <Link to="/" className="text-body-md underline">
            <Icon name="arrow-left">Back to home</Icon>
          </Link>
        </div>
      )}
    />
  );
}
